import type { CSSProperties } from 'react';
import { resolveLogoUrl } from '@/routes/outil-pro/features/societies/utils/logo.utils';
import type { PaymentTokenMetadata, PaymentTokenStripeData } from '../api/paymentApi';

/** Carte blanche de la page (récapitulatif, paiement, écrans d'état). */
export const PAYMENT_CARD_CLASS =
  'tw-rounded-2xl tw-bg-white tw-shadow-lg tw-shadow-slate-900/5 tw-ring-1 tw-ring-slate-200';

/** Ancre du récapitulatif (bouton « Détail » du résumé mobile). */
export const SUMMARY_SECTION_ID = 'recapitulatif';

/** Couleurs Affinicar (tailwind.config : affinicar.primary / affinicar.secondary). */
const AFFINICAR_PRIMARY = '#8074af';
const AFFINICAR_SECONDARY = '#575fa7';
/** Texte foncé de repli quand aucune couleur de marque n'est lisible sur fond blanc. */
const NEUTRAL_INK = '#1f2937';

/**
 * Identité visuelle de la page de paiement : garant (contrat upsell) ou, à défaut,
 * société du contrat avec les couleurs Affinicar.
 */
export interface PaymentTheme {
  /** Nom affiché : garant pour un upsell, sinon société du contrat. */
  name: string | null;
  logoUrl: string | null;
  /** true = couleurs du garant ; false = couleurs Affinicar. */
  branded: boolean;
  primaryColor: string;
  primaryTextColor: string;
  accentColor: string;
  accentTextColor: string;
  /** Couleur de marque lisible sur fond blanc (icônes, liens, montants). */
  inkColor: string;
}

export const DEFAULT_PAYMENT_THEME: PaymentTheme = {
  name: null,
  logoUrl: null,
  branded: false,
  primaryColor: AFFINICAR_PRIMARY,
  primaryTextColor: '#ffffff',
  accentColor: AFFINICAR_PRIMARY,
  accentTextColor: '#ffffff',
  inkColor: AFFINICAR_SECONDARY,
};

function hexToRgb(hex: string): [number, number, number] | null {
  const value = hex.trim().replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  const n = Number.parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Couleur #RRGGBB → rgba() avec transparence (renvoie la valeur telle quelle si illisible). */
export function withAlpha(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : hex;
}

/** Ratio de contraste WCAG d'une couleur avec le blanc (0 si couleur illisible). */
function contrastWithWhite(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return 1.05 / (luminance + 0.05);
}

/** Première couleur de marque assez contrastée pour du texte sur fond blanc (AA). */
function pickInkColor(candidates: string[]): string {
  return candidates.find((color) => contrastWithWhite(color) >= 4.5) ?? NEUTRAL_INK;
}

export function buildPaymentTheme(data: PaymentTokenStripeData): PaymentTheme {
  const branding = data.upsell?.branding;
  if (branding) {
    const primaryColor = branding.primaryColor || AFFINICAR_PRIMARY;
    const accentColor = branding.accentColor || primaryColor;
    return {
      name: branding.name || null,
      logoUrl: branding.logoUrl,
      branded: true,
      primaryColor,
      primaryTextColor: branding.primaryTextColor || '#ffffff',
      accentColor,
      accentTextColor: branding.accentTextColor || '#ffffff',
      inkColor: pickInkColor([accentColor, primaryColor]),
    };
  }

  const metadata = data.metadata;
  const logoUrl =
    metadata?.companyLogo && metadata.companyCode
      ? resolveLogoUrl({ code: metadata.companyCode, logo: metadata.companyLogo })
      : null;
  return { ...DEFAULT_PAYMENT_THEME, name: metadata?.companyName || null, logoUrl };
}

/** Variables CSS consommées par les composants de la page (`tw-*-[color:var(--pay-…)]`). */
export function themeCssVars(theme: PaymentTheme): CSSProperties {
  return {
    '--pay-accent': theme.accentColor,
    '--pay-accent-text': theme.accentTextColor,
    '--pay-accent-soft': withAlpha(theme.accentColor, 0.08),
    '--pay-ink': theme.inkColor,
  } as CSSProperties;
}

/** Interlocuteur du payeur : Affinicar pour le compte du garant (upsell) ou société du contrat. */
export interface PaymentContact {
  name: string | null;
  /** Société pour le compte de laquelle l'interlocuteur vend (le garant, en upsell). */
  onBehalfOf: string | null;
  phone: string | null;
  email: string | null;
}

export function buildPaymentContact(data: PaymentTokenStripeData): PaymentContact {
  const upsell = data.upsell;
  if (upsell?.contact) {
    return {
      name: upsell.contact.name,
      onBehalfOf: upsell.branding.name || null,
      phone: upsell.contact.phone,
      email: upsell.contact.email,
    };
  }
  return { name: upsell?.branding.name || data.metadata?.companyName || null, onBehalfOf: null, phone: null, email: null };
}

/** « Affinicar, pour le compte de CARDIS AUTOMOBILES », ou le seul nom de l'interlocuteur. */
export function contactDisplayName(contact: PaymentContact): string {
  const name = contact.name ?? 'votre professionnel';
  return contact.onBehalfOf ? `${name}, pour le compte de ${contact.onBehalfOf}` : name;
}

/** Formulaire affiché : même règle que le choix du formulaire Stripe. */
export type PaymentMethodKind = 'sepa_mandate' | 'sepa_single' | 'card';

export function getPaymentMethodKind(data: Pick<PaymentTokenStripeData, 'intentType' | 'paymentMethod'>): PaymentMethodKind {
  if (data.intentType === 'setup_intent') return 'sepa_mandate';
  return data.paymentMethod === 'sepa_debit' ? 'sepa_single' : 'card';
}

export type PaymentAmounts =
  | {
      kind: 'monthly';
      monthlyAmount: number;
      installments: number | null;
      total: number | null;
      serviceFees: number | null;
    }
  | { kind: 'single'; total: number };

/** Montants à afficher, lus dans les métadonnées du jeton (euros). */
export function buildPaymentAmounts(
  intentType: PaymentTokenStripeData['intentType'],
  metadata: PaymentTokenMetadata | null,
): PaymentAmounts | null {
  if (!metadata) return null;
  if (intentType === 'setup_intent') {
    if (metadata.monthlyAmount == null) return null;
    return {
      kind: 'monthly',
      monthlyAmount: metadata.monthlyAmount,
      installments: metadata.duration ?? null,
      total: metadata.totalAmount ?? null,
      serviceFees: metadata.serviceFees != null && metadata.serviceFees > 0 ? metadata.serviceFees : null,
    };
  }
  const total = metadata.sellingPrice ?? metadata.totalAmount;
  return total != null ? { kind: 'single', total } : null;
}

export function getVehicleLabel(metadata: PaymentTokenMetadata | null): string | null {
  const label = [metadata?.vehicleBrand, metadata?.vehicleModel].filter(Boolean).join(' ');
  return label || null;
}

const euroFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
const wholeEuroFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/** Montant au centime près : « 29,90 € ». */
export const formatEuro = (amount: number): string => euroFormatter.format(amount);

/** Plafond : sans décimales quand le montant est rond (« 5 000 € »). */
export const formatEuroCap = (amount: number): string =>
  Number.isInteger(amount) ? wholeEuroFormatter.format(amount) : euroFormatter.format(amount);

export const formatKm = (km: number): string => `${km.toLocaleString('fr-FR')} km`;

export const formatMonths = (months: number): string => `${months} mois`;

export const formatDays = (days: number): string => `${days} jour${days > 1 ? 's' : ''}`;

export function formatLongDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Lien tel: à partir d'un numéro saisi librement (espaces, points…). */
export function toTelHref(phone: string): string {
  const digits = Array.from(phone).filter((c) => c === '+' || (c >= '0' && c <= '9')).join('');
  return `tel:${digits}`;
}

export type TokenErrorKind = 'expired' | 'used' | 'error';

/** Traduit l'erreur de résolution du jeton en écran (mêmes règles qu'avant). */
export function getTokenError(error: unknown): { kind: TokenErrorKind; message: string } {
  const raw = (error as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  const message = typeof raw === 'string' ? raw : '';
  if (message.includes('expiré')) return { kind: 'expired', message };
  if (message.includes('déjà été utilisé')) return { kind: 'used', message };
  return { kind: 'error', message: message || 'Impossible de charger le lien de paiement.' };
}
